import { ChevronLeft, ChevronRight, Phone, RotateCw, Star } from "lucide-react";
import * as React from "react";
import { toneBlock } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Callout, Spinner } from "@/components/ui/feedback";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import type { ViewFilters } from "@/features/manage-view";
import { isClosed, statusInfo } from "@/lib/appointments";
import { browserZone, zoneLabel } from "@/lib/booking-time";
import { CALLBACK_MINUTES, calendarTimes, forSkilled, whoTakesCalendarCallback } from "@/lib/callbacks";
import {
  VIEW_LABELS, gmtLabel, layoutDay, onDay, rangeLabel, sameDay, step, viewRange, type PickedTime, type Timed, type ViewMode,
} from "@/lib/calendar-view";
import { useAppointments, useCalendarBlocks, useCalendarCallbacks } from "@/lib/queries";
import type { AppointmentListItem, CalendarBlock, CalendarCallback } from "@/lib/types";
import { cn } from "@/lib/utils";

const HOUR_PX = 48;
const MONTH_CHIPS = 4;

type Item = Timed &
  (
    | { kind: "appointment"; appointment: AppointmentListItem }
    | { kind: "block"; block: CalendarBlock }
    /** `backup`: which of its backup times this is; 0 is the callback's own time. */
    | { kind: "callback"; callback: CalendarCallback; backup: number }
  );

/** In its status's colour, as its badge is, with a stripe down the left edge as GoHighLevel's
 *  calendar had (ACL-525); on the bare surface and struck through once cancelled or moved; grey
 *  and hatched for time blocked off. */
function itemClass(item: Item): string {
  if (item.kind === "callback") {
    const { callback: cb, backup } = item;
    if (backup > 0) return "border border-dashed border-purple-9 bg-surface text-purple-11";
    if (cb.status === "missed") return "border-l-2 border-danger-9 bg-danger-2 text-danger-11 line-through";
    if (cb.status === "cancelled") return "border-l-2 border-gray-7 bg-surface text-text-muted line-through";
    if (cb.status === "done") return "border-l-2 border-gray-7 bg-gray-2 text-text-muted";
    return forSkilled(cb)
      ? "border-l-2 border-accent-9 bg-accent-3 text-accent-11"
      : "border-l-2 border-purple-9 bg-purple-2 text-purple-11";
  }
  if (item.kind === "block") {
    return "border border-gray-6 text-text-muted bg-[repeating-linear-gradient(135deg,var(--color-gray-3)_0_6px,var(--color-gray-4)_6px_12px)]";
  }
  const { status } = item.appointment;
  return cn(toneBlock(statusInfo(status).tone), isClosed(status) && "bg-surface line-through");
}

function itemTitle(item: Item): string {
  if (item.kind === "callback") return item.callback.contact_name ?? "Callback";
  if (item.kind === "block") return item.block.title || `Blocked · ${item.block.user_name ?? item.block.calendar_name ?? ""}`;
  return item.appointment.title;
}

/** A callback's mark before its name, as the legend says it. */
function ItemMark({ item }: { item: Item }) {
  if (item.kind !== "callback") return null;
  const Icon = item.backup > 0 ? RotateCw : forSkilled(item.callback) ? Star : Phone;
  return <Icon className="mr-1 inline size-3 align-[-2px]" aria-hidden />;
}

function hover(item: Item, time: (d: Date) => string): string {
  const when = `${time(item.start)} – ${time(item.end)}`;
  if (item.kind === "callback") {
    return [
      itemTitle(item),
      time(item.start),
      item.backup > 0 ? "Its backup time, if nobody answers" : item.callback.purpose,
      whoTakesCalendarCallback(item.callback),
    ]
      .filter(Boolean)
      .join("\n");
  }
  if (item.kind === "block") {
    return `${itemTitle(item)}\n${when}\nBlocked off for ${item.block.user_name ?? item.block.calendar_name}`;
  }
  const a = item.appointment;
  return [a.title, when, a.contact_name, a.owner_name, `${a.calendar_name} · ${statusInfo(a.status).label}`]
    .filter(Boolean)
    .join("\n");
}

/**
 * The calendar view (ACL-292): Day, Week and Month, on the viewer's clock, with the same filters
 * as the list. An appointment opens its details; a block and a callback open their own. Callbacks are
 * on no calendar, so narrowing to a calendar leaves them out (ACL-658). A month day with more than
 * four opens as a day. Clicking empty space books there, as GoHighLevel does: a month day gives the
 * date, the hour grids the half hour clicked.
 */
export function CalendarBoard({
  filters, onOpenAppointment, onOpenBlock, onOpenCallback, onPick, toolbarEnd,
}: {
  filters: ViewFilters;
  onOpenAppointment: (id: number) => void;
  onOpenBlock: (block: CalendarBlock) => void;
  onOpenCallback: (callback: CalendarCallback) => void;
  onPick: (picked: PickedTime) => void;
  toolbarEnd?: React.ReactNode;
}) {
  const [mode, setMode] = React.useState<ViewMode>("week");
  const [anchor, setAnchor] = React.useState(() => new Date());
  const range = viewRange(mode, anchor);
  const span = { start: range.start.toISOString(), end: range.end.toISOString() };
  const owners = {
    user_ids: filters.userIds.join(",") || undefined,
    calendar_ids: filters.calendarIds.join(",") || undefined,
  };
  const showAppointments = filters.type === "all" || filters.type === "appointments";
  const showBlocks = filters.type === "all" || filters.type === "blocks";
  const showCallbacks = (filters.type === "all" || filters.type === "callbacks") && filters.calendarIds.length === 0;
  const appointments = useAppointments(
    { ...span, ...owners, statuses: filters.statuses.join(",") || undefined, limit: 1000 },
    { enabled: showAppointments },
  );
  const blocks = useCalendarBlocks({ ...span, ...owners }, { enabled: showBlocks });
  const callbacks = useCalendarCallbacks(
    { ...span, user_ids: owners.user_ids, limit: 1000 },
    { enabled: showCallbacks },
  );

  const items: Item[] = React.useMemo(() => {
    const out: Item[] = [];
    if (showCallbacks) {
      for (const callback of callbacks.data ?? []) {
        for (const { at, backup } of calendarTimes(callback)) {
          out.push({
            kind: "callback",
            callback,
            backup,
            id: `c${callback.id}-${backup}`,
            start: new Date(at),
            end: new Date(at + CALLBACK_MINUTES * 60_000),
          });
        }
      }
    }
    if (showAppointments) {
      for (const a of appointments.data ?? []) {
        out.push({ kind: "appointment", appointment: a, id: `a${a.id}`, start: new Date(a.starts_at), end: new Date(a.ends_at) });
      }
    }
    if (showBlocks) {
      for (const b of blocks.data ?? []) {
        out.push({ kind: "block", block: b, id: `b${b.id}`, start: new Date(b.starts_at), end: new Date(b.ends_at) });
      }
    }
    return out;
  }, [appointments.data, blocks.data, callbacks.data, showAppointments, showBlocks, showCallbacks]);

  const open = (item: Item) => {
    if (item.kind === "appointment") onOpenAppointment(item.appointment.id);
    else if (item.kind === "block") onOpenBlock(item.block);
    else onOpenCallback(item.callback);
  };
  const time = (d: Date) => new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(d);
  const loading =
    (showAppointments && appointments.isPending) || (showBlocks && blocks.isPending) || (showCallbacks && callbacks.isPending);
  // An older core-api has no callbacks to list: the calendar still shows the rest.
  const failed = (showAppointments && appointments.error) || (showBlocks && blocks.error);

  return (
    <div className="min-w-0 flex-1 space-y-3">
      {showCallbacks && !callbacks.error && <Legend appointments={showAppointments} />}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={() => setAnchor(new Date())}>
          Today
        </Button>
        <span className="inline-flex items-center rounded-md border border-border">
          <Button variant="ghost" size="icon" aria-label="Previous" onClick={() => setAnchor(step(mode, anchor, -1))}>
            <ChevronLeft />
          </Button>
          <span className="min-w-44 px-2 text-center text-sm font-medium text-text">{rangeLabel(mode, anchor)}</span>
          <Button variant="ghost" size="icon" aria-label="Next" onClick={() => setAnchor(step(mode, anchor, 1))}>
            <ChevronRight />
          </Button>
        </span>
        <Select value={mode} onValueChange={(m) => setMode(m as ViewMode)}>
          <SelectTrigger className="w-36" aria-label="View">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(["day", "week", "month"] as const).map((m) => (
              <SelectItem key={m} value={m}>
                {VIEW_LABELS[m]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ZoneNote />
        {(appointments.isFetching || blocks.isFetching || callbacks.isFetching) && !loading && <Spinner className="size-4" />}
        <span className="ml-auto flex items-center gap-2">{toolbarEnd}</span>
      </div>

      {failed ? (
        <Callout tone="danger">Could not load the calendar. Please try again.</Callout>
      ) : loading ? (
        <div className="grid place-items-center rounded-xl border border-border py-24">
          <Spinner />
        </div>
      ) : mode === "month" ? (
        <MonthGrid
          days={range.days}
          anchor={anchor}
          items={items}
          onOpen={open}
          onPick={(d) => onPick({ at: d, withTime: false })}
          hover={(i) => hover(i, time)}
          onDay={(d) => {
            setAnchor(d);
            setMode("day");
          }}
        />
      ) : (
        <HourGrid
          days={range.days}
          items={items}
          onOpen={open}
          onPick={(at) => onPick({ at, withTime: true })}
          hover={(i) => hover(i, time)}
          time={time}
        />
      )}
    </div>
  );
}

/** What a callback looks like on the board, since four kinds sit beside appointments. */
function Legend({ appointments }: { appointments: boolean }) {
  const stripe = "inline-block h-3 w-1 rounded-sm";
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md bg-gray-2 px-3 py-1.5 text-xs text-text-muted">
      <span className="flex items-center gap-1.5">
        <span className={cn(stripe, "bg-purple-9")} />
        <Phone className="size-3" aria-hidden />
        Regular callback
      </span>
      <span className="flex items-center gap-1.5">
        <span className={cn(stripe, "bg-accent-9")} />
        <Star className="size-3" aria-hidden />
        Closer callback
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block size-3 rounded-sm border border-dashed border-purple-9" />
        <RotateCw className="size-3" aria-hidden />
        Its backup time, if nobody answers
      </span>
      <span className="flex items-center gap-1.5">
        <span className={cn(stripe, "bg-danger-9")} />
        Missed
      </span>
      {appointments && (
        <span className="flex items-center gap-1.5">
          <span className={cn(stripe, "bg-success-9")} />
          Appointment
        </span>
      )}
    </div>
  );
}

/** The board draws times on this computer's clock, which need not be a calendar's (ACL-521): it
 *  says which, as booking an appointment then lets the booker choose. */
function ZoneNote() {
  const zone = browserZone();
  if (!zone) return null;
  return (
    <span className="text-xs text-text-muted" title="Booking an appointment offers the calendar's timezone, and others.">
      Times in your timezone: {zoneLabel(zone)}
    </span>
  );
}

function MonthGrid({
  days, anchor, items, onOpen, onPick, hover, onDay: openDay,
}: {
  days: Date[];
  anchor: Date;
  items: Item[];
  onOpen: (i: Item) => void;
  onPick: (d: Date) => void;
  hover: (i: Item) => string;
  onDay: (d: Date) => void;
}) {
  const today = new Date();
  const weekdays = days.slice(0, 7).map((d) => new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(d));
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface">
      <div className="grid grid-cols-7 border-b border-border">
        {weekdays.map((w) => (
          <div key={w} className="py-2 text-center text-sm font-medium text-text-muted">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const here = onDay(items, d);
          const outside = d.getMonth() !== anchor.getMonth();
          return (
            <div
              key={d.toISOString()}
              className="min-h-28 cursor-pointer border-b border-r border-border p-1 hover:bg-surface-hover [&:nth-child(7n)]:border-r-0"
              title="Book on this day"
              onClick={() => onPick(d)}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openDay(d);
                }}
                className={cn(
                  "mb-1 ml-auto block rounded px-1.5 text-xs",
                  sameDay(d, today) ? "bg-accent-9 font-semibold text-white" : outside ? "text-text-subtle" : "text-text-muted hover:text-text",
                )}
              >
                {d.getDate()}
              </button>
              <div className="space-y-0.5">
                {here.slice(0, MONTH_CHIPS).map((i) => (
                  <button
                    key={i.id}
                    type="button"
                    title={hover(i)}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpen(i);
                    }}
                    className={cn("block w-full cursor-pointer truncate rounded px-1.5 py-0.5 text-left text-xs", itemClass(i))}
                  >
                    <ItemMark item={i} />
                    {itemTitle(i)}
                  </button>
                ))}
                {here.length > MONTH_CHIPS && (
                  <button
                    type="button"
                    className="px-1.5 text-xs font-medium text-accent-11 hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDay(d);
                    }}
                  >
                    +{here.length - MONTH_CHIPS} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HourGrid({
  days, items, onOpen, onPick, hover, time,
}: {
  days: Date[];
  items: Item[];
  onOpen: (i: Item) => void;
  onPick: (at: Date) => void;
  hover: (i: Item) => string;
  time: (d: Date) => string;
}) {
  const scroller = React.useRef<HTMLDivElement>(null);
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    // Open on the working day rather than on midnight.
    scroller.current?.scrollTo({ top: 7 * HOUR_PX });
  }, []);
  React.useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(t);
  }, []);
  const hours = Array.from({ length: 24 }, (_, h) => h);
  const hourLabel = (h: number) => new Intl.DateTimeFormat(undefined, { hour: "numeric" }).format(new Date(2000, 0, 1, h));
  const cols = `4rem repeat(${days.length}, minmax(0, 1fr))`;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface">
      <div className="grid border-b border-border" style={{ gridTemplateColumns: cols }}>
        <div className="py-2 text-center text-2xs text-text-subtle" title={browserZone()?.replace(/_/g, " ")}>
          {gmtLabel(days[0]!)}
        </div>
        {days.map((d) => (
          <div
            key={d.toISOString()}
            className={cn("py-2 text-center text-sm font-medium", sameDay(d, now) ? "text-accent-11" : "text-text-muted")}
          >
            {new Intl.DateTimeFormat(undefined, { day: "2-digit" }).format(d)}{" "}
            {new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(d)}
          </div>
        ))}
      </div>
      <div ref={scroller} className="max-h-[70vh] overflow-y-auto">
        <div className="relative grid" style={{ gridTemplateColumns: cols, height: 24 * HOUR_PX }}>
          <div>
            {hours.map((h) => (
              <div key={h} className="pr-2 text-right text-2xs text-text-subtle" style={{ height: HOUR_PX }}>
                {h === 0 ? "" : hourLabel(h)}
              </div>
            ))}
          </div>
          {days.map((d) => (
            <div
              key={d.toISOString()}
              className="relative cursor-pointer border-l border-border"
              title="Book at this time"
              onClick={(e) => {
                // The half hour under the pointer, on this day.
                const y = e.clientY - e.currentTarget.getBoundingClientRect().top;
                const minutes = Math.min(Math.max(Math.floor((y / HOUR_PX) * 2), 0), 47) * 30;
                onPick(new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, minutes));
              }}
            >
              {hours.map((h) => (
                <div key={h} className="border-b border-border/60" style={{ height: HOUR_PX }} />
              ))}
              {sameDay(d, now) && (
                <div
                  className="pointer-events-none absolute inset-x-0 z-10 border-t-2 border-danger-9"
                  style={{ top: ((now.getHours() * 60 + now.getMinutes()) / 60) * HOUR_PX }}
                  aria-hidden
                />
              )}
              {layoutDay(items, d).map(({ item, top, height, column, columns }) => (
                <button
                  key={item.id}
                  type="button"
                  title={hover(item)}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(item);
                  }}
                  className={cn("absolute cursor-pointer overflow-hidden rounded px-1.5 py-0.5 text-left text-xs leading-tight", itemClass(item))}
                  style={{
                    top: (top / 60) * HOUR_PX,
                    height: Math.max((height / 60) * HOUR_PX - 2, 14),
                    left: `calc(${(column / columns) * 100}% + 2px)`,
                    width: `calc(${100 / columns}% - 4px)`,
                  }}
                >
                  <span className="block truncate font-medium">
                    <ItemMark item={item} />
                    {itemTitle(item)}
                  </span>
                  {height >= 30 && (
                    <span className="block truncate opacity-80">
                      {time(item.start)}
                      {item.kind === "appointment" && ` · ${statusInfo(item.appointment.status).label}`}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
